import React, { useState } from "react";
import {
  Stack,
  Text,
  Paper,
  Group,
  Badge,
  ActionIcon,
  Button,
} from "@mantine/core";
import { JourneyItem } from "./journey.types";
import { IconTimeline, IconEdit, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

interface Props {
  journeys: JourneyItem[];
  openId: string | null;
  onToggle: (id: string | null) => void;
  onRefetch: () => void;
  onEditJourney?: (journey: JourneyItem) => void;
  onDeleteJourney?: (journeyId: string) => void;
  isSuperAdmin?: boolean;
}

const JourneyTimeline = ({
  journeys,
  openId,
  onToggle,
  onRefetch,
  onEditJourney,
  onDeleteJourney,
  isSuperAdmin = false,
}: Props) => {
  const [showAll, setShowAll] = useState(false);

  const handleEdit = (journey: JourneyItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditJourney) {
      onEditJourney(journey);
    }
  };

  const handleDelete = async (journeyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteJourney) {
      onDeleteJourney(journeyId);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // const getRelativeTime = (index: number) => {
  //   if (index === 0) return "Latest Entry";
  //   if (index === 1) return "1 Day(s) Ago";
  //   return `${index} Day(s) Ago`;
  // };

  // Show last 3 entries by default, all when expanded
  const INITIAL_COUNT = 3;
  const displayedJourneys = showAll
    ? journeys
    : journeys.slice(0, INITIAL_COUNT);
  const hasMore = journeys.length > INITIAL_COUNT;

  return (
    <Paper shadow="sm" className="p-4 border border-[#FC7A69]/20" radius="lg">
      <div className="flex items-center gap-2 mb-1">
        <IconTimeline size={20} className="text-[#FC7A69]" />
        <Text className="text-sm font-bold text-[#221E45]">
          Journey History
        </Text>
        <Badge size="sm" className="bg-[#FC7A69]/10 text-[#FC7A69]">
          {journeys.length} {journeys.length === 1 ? "Entry" : "Entries"}
        </Badge>
      </div>
      {!showAll && hasMore && (
        <Text size="xs" c="dimmed" mb="sm">
          Showing last {INITIAL_COUNT} entries
        </Text>
      )}

      <Stack gap="md">
        {displayedJourneys.map((journey, index) => (
          <Paper
            key={journey.journey_id}
            p="md"
            withBorder
            radius="md"
            className={`
              cursor-pointer transition-all duration-200
              ${
                index < INITIAL_COUNT
                  ? "border-l-4 border-l-[#FC7A69] bg-[#FC7A69]/5"
                  : "border-l-4 border-l-gray-300 hover:border-l-[#FC7A69]/50"
              }
              ${openId === journey.journey_id ? "shadow-md" : "hover:shadow-sm"}
            `}
            onClick={() =>
              onToggle(
                openId === journey.journey_id ? null : journey.journey_id,
              )
            }
          >
            {/* Header */}
            <Group justify="space-between" wrap="nowrap">
              <div className="flex-1">
                <Group gap="xs" wrap="wrap">
                  <Text className="text-sm font-bold text-[#221E45]">
                    {formatDate(journey.journeyDate)}
                  </Text>
                  <Text className="text-xs text-[#221E45]/60">
                    {formatDate(journey.journeyDate)}
                  </Text>
                  <Badge
                    size="sm"
                    className={
                      journey.batchStatus === "On-going"
                        ? "bg-orange-100 text-orange-700"
                        : journey.batchStatus === "Completed"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                    }
                  >
                    {journey.batchStatus}
                  </Badge>
                  {/* Issue tracking badges */}
                  {journey.is_issue && journey.issue_status !== "resolved" && (
                    <div className="flex flex-col gap-1">
                      <Badge size="sm" color="red" variant="filled">
                        ⚠ Issue
                      </Badge>
                      {journey.issue_description && (
                        <div className="flex items-start gap-1 bg-red-50 border border-red-200 rounded-md px-2 py-1 max-w-xs">
                          <span className="text-red-500 mt-0.5 text-xs">📋</span>
                          <Text size="xs" className="text-red-700 leading-snug">
                            {journey.issue_description}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                  {journey.is_issue && journey.issue_status === "resolved" && (
                    <div className="flex flex-col gap-1">
                      <Badge size="sm" color="green" variant="filled">
                        ✓ Resolved
                      </Badge>
                      {journey.resolve_comment && (
                        <div className="flex items-start gap-1 bg-green-50 border border-green-200 rounded-md px-2 py-1 max-w-xs">
                          <span className="text-green-600 mt-0.5 text-xs">
                            💬
                          </span>
                          <Text
                            size="xs"
                            className="text-green-700 leading-snug"
                          >
                            {journey.resolve_comment}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Not Started badge */}
                  {journey.changeSummary === "Not Started" && (
                    <Badge size="sm" color="grape" variant="filled">
                      🔧 Not Started
                    </Badge>
                  )}
                  {/* Not Schedule badge */}
                  {journey.changeSummary === "Not Schedule" && (
                    <Badge size="sm" color="indigo" variant="filled">
                      📅 Not Schedule
                    </Badge>
                  )}
                </Group>
              </div>

              {/* Action Buttons — Edit visible only for SuperAdmin */}
              <Group gap="xs">
                {isSuperAdmin && (
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    size="sm"
                    onClick={(e) => handleEdit(journey, e)}
                    title="Edit Journey"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                )}
              </Group>
            </Group>

            {/* Summary Preview */}
            <Text size="xs" className="text-[#221E45]/70 mt-2 line-clamp-2">
              {journey.changeSummary || "No summary provided"}
            </Text>

            {/* Expanded Details */}
            {openId === journey.journey_id && (
              <div className="mt-4 pt-4 border-t border-[#FC7A69]/20">
                <Stack gap="sm">
                  {/* Sessions & Trainer */}
                  <Group gap="lg">
                    <div>
                      <Text size="xs" className="text-[#221E45]/60">
                        Sessions
                      </Text>
                      <Text size="sm" fw={600} className="text-[#221E45]">
                        {journey.numSessions}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" className="text-[#221E45]/60">
                        Trainer
                      </Text>
                      <Text size="sm" fw={600} className="text-[#221E45]">
                        {journey.trainerName}
                      </Text>
                    </div>
                  </Group>

                  {/* Students */}
                  {journey.students && journey.students.length > 0 && (
                    <div>
                      <Text size="xs" className="text-[#221E45]/60 mb-1">
                        Students ({journey.students.length})
                      </Text>
                      <div className="flex flex-wrap gap-1">
                        {journey.students
                          .slice(0, 5)
                          .map((student: any, idx: number) => (
                            <Badge
                              key={idx}
                              size="xs"
                              className="bg-blue-100 text-blue-700"
                            >
                              {student.name}
                            </Badge>
                          ))}
                        {journey.students.length > 5 && (
                          <Badge
                            size="xs"
                            className="bg-gray-100 text-gray-700"
                          >
                            +{journey.students.length - 5} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Instructors */}
                  {/* Instructors */}
                  {journey.instructors && journey.instructors.length > 0 && (
                    <div>
                      <Text size="xs" className="text-[#221E45]/60 mb-1">
                        Instructors ({journey.instructors.length})
                      </Text>
                      <div className="flex flex-wrap gap-1">
                        {journey.instructors.map(
                          (instructor: any, idx: number) => {
                            //  Check if this is the active trainer
                            const isActive =
                              instructor.email.toLowerCase() ===
                              journey.trainerEmail.toLowerCase();

                            return (
                              <Badge
                                key={idx}
                                size="xs"
                                className={
                                  isActive
                                    ? "bg-purple-100 text-purple-700 border border-purple-300"
                                    : "bg-gray-100 text-gray-600"
                                }
                              >
                                {instructor.name} (
                                {instructor.instructorClasses || "0"})
                                {isActive && " ★"}
                              </Badge>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}

                  {/* Full Summary */}
                  <div>
                    <Text size="xs" className="text-[#221E45]/60 mb-1">
                      Full Summary
                    </Text>
                    <Text size="xs" className="text-[#221E45]/80">
                      {journey.changeSummary || "No summary provided"}
                    </Text>
                  </div>
                </Stack>
              </div>
            )}
          </Paper>
        ))}
      </Stack>

      {hasMore && (
        <div className="text-center mt-3">
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setShowAll(!showAll)}
            className="text-[#FC7A69] hover:bg-[#FC7A69]/10"
          >
            {showAll
              ? `Show Less (Last ${INITIAL_COUNT})`
              : `Show All ${journeys.length} Entries`}
          </Button>
        </div>
      )}

      {journeys.length === 0 && (
        <div className="text-center py-8">
          <Text className="text-sm text-[#221E45]/60">
            No journey entries yet
          </Text>
        </div>
      )}
    </Paper>
  );
};

export default JourneyTimeline;

// import React from "react";
// import { Stack, Text, Paper } from "@mantine/core";
// import { IconTimeline } from "@tabler/icons-react";
// import JourneyCard from "./JourneyCard";
// import { JourneyItem } from "./journey.types";

// interface Props {
//   journeys: JourneyItem[];
//   openId: string | null;
//   onToggle: (id: string | null) => void;
//   onRefetch: () => void;
//   onEditJourney?: (journey: JourneyItem) => void;
//   onDeleteJourney?: (journeyId: string) => void;
// }

// const JourneyTimeline = ({
//   journeys,
//   openId,
//   onToggle,
//   onRefetch,
//   onEditJourney,
//   onDeleteJourney,
// }: Props) => {
//   const handleEdit = (journey: JourneyItem) => {
//     if (onEditJourney) {
//       onEditJourney(journey);
//     }
//   };

//   const handleDelete = async (journeyId: string) => {
//     if (onDeleteJourney) {
//       await onDeleteJourney(journeyId);
//       onRefetch();
//     }
//   };

//   return (
//     <Paper shadow="sm" className="p-4 border border-[#FC7A69]/20" radius="lg">
//       {/* Header */}
//       <div className="flex items-center gap-2 mb-4">
//         <IconTimeline size={20} className="text-[#FC7A69]" />
//         <Text className="text-sm font-bold text-[#221E45]">
//           Journey History
//         </Text>
//       </div>

//       {/* Timeline */}
//       <Stack gap="md">
//         {journeys.map((journey, index) => (
//           <JourneyCard
//             key={journey.journey_id}
//             journey={journey}
//             index={index}
//             isOpen={openId === journey.journey_id}
//             onClick={() =>
//               onToggle(
//                 openId === journey.journey_id ? null : journey.journey_id,
//               )
//             }
//             onEdit={handleEdit}
//             onDelete={
//               index !== 0 ? () => handleDelete(journey.journey_id) : undefined
//             }
//           />
//         ))}
//       </Stack>

//       {/* Empty State */}
//       {journeys.length === 0 && (
//         <div className="text-center py-8">
//           <Text className="text-sm text-[#221E45]/60">
//             No journey entries yet
//           </Text>
//         </div>
//       )}
//     </Paper>
//   );
// };

// export default JourneyTimeline;
